import { useEffect, useState } from "react"

import { Button, Modal } from "antd"
import { BoardToolbarAction } from "shared/constants/toolbar"
import { selectModalType } from "features/common/model/selectors/modalSelectors"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { CopyOutlined, DisconnectOutlined } from "shared/ui"
import { createGlobalStyle, styled } from "styled-components"
import { NOTIFICATION_TYPES, showNotification } from "features/common/notifications"

import { selectBoardsSharingAvailableTypesPublicLinkSelector, selectBoardsSharingBoardDataSelector, selectBoardsSharingDataSelector } from "../../../model/selectors/sharingSelectors"
import { closeModal } from "../../../model/slice/moadlSlice"
import { addAvailableTypesPublicLinkThunk, deleteSharedLinkThunk, fetchCollaboratorsThunk, fetchShareDataThunk, getSharingSettingsThunk, sharingActions } from "../../../model/slice/sharingSlice"

import { SettingsTab } from "./SettingTab"

const ConfirmModalStyles = createGlobalStyle`
  .confirm-modal-ok-button {
    background-color: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
    color: white !important;
  }
  
  .confirm-modal-ok-button:hover {
    background-color: var(--ant-color-primary-hover) !important;
    border-color: var(--ant-color-primary-hover) !important;
    color: white !important;
  }
  
  .confirm-modal-cancel-button:hover {
    border-color: var(--ant-color-primary-hover) !important;
    color: var(--ant-color-primary-hover) !important;
  }
`

export const ShareLinkItemsModal = () => {
  const modalType = useAppSelector(selectModalType)
  const isShareLinkBoardModalOpen = modalType === BoardToolbarAction.Share
  const dispatch = useAppDispatch()
  const availableTypesPublicLink = useAppSelector(selectBoardsSharingAvailableTypesPublicLinkSelector)
  const sharedItemData = useAppSelector(selectBoardsSharingBoardDataSelector)
  const sharingData = useAppSelector(selectBoardsSharingDataSelector)
  const publicLink = sharingData?._links?.public_url?.href
  const [pendingCollaborator, setPendingCollaborator] = useState('')
  // const [permission, setPermission] = useState<"read" | "write">("read")

  const handleClose = () => {
    if (pendingCollaborator) {
      Modal.confirm({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to close?',
        onOk: () => dispatch(closeModal()),
        okText: 'Yes',
        cancelText: 'No',
        okButtonProps: {
          className: 'confirm-modal-ok-button'
        },
        cancelButtonProps: {
          className: 'confirm-modal-cancel-button'
        }
      })
    } else {
      dispatch(closeModal())
      dispatch(sharingActions.resetState())
      dispatch(sharingActions.resetSharingSettings())
    }
  }

  const handleCopyLink = () => {
    const copy = () => {
      if (publicLink) {
        navigator.clipboard.writeText(publicLink)
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: "",
          message: "Link copied"
        })
        dispatch(closeModal())
      }
    }

    if (pendingCollaborator) {
      Modal.confirm({
        title: 'Unsaved changes',
        content: 'This will copy the link, but your unsaved changes will be lost. Continue?',
        onOk: copy,
        okText: 'Yes',
        cancelText: 'No',
        okButtonProps: {
          className: 'confirm-modal-ok-button'
        },
        cancelButtonProps: {
          className: 'confirm-modal-cancel-button'
        }
      })
    } else {
      copy()
    }
  }

  const handleDeleteLink = () => {
    if (sharedItemData) {
      dispatch(deleteSharedLinkThunk({ data: sharedItemData, callback: handleClose }))
    }
  }

  useEffect(() => {
    if (isShareLinkBoardModalOpen) {
      dispatch(fetchCollaboratorsThunk())
    }
  }, [isShareLinkBoardModalOpen, dispatch])

  useEffect(() => {
    if (availableTypesPublicLink.length === 0) {
      dispatch(addAvailableTypesPublicLinkThunk())
    }

    if (!sharedItemData) {
      return
    }

    dispatch(fetchShareDataThunk())
  }, [sharedItemData?._links?.share + sharedItemData?.id])

    
  useEffect(() => {
    if (sharedItemData?.id) {
      dispatch(getSharingSettingsThunk(sharedItemData?.id))
    }

    return () => {
      dispatch(sharingActions.resetSharingSettings())
    }
  }, [sharedItemData?.id])

  return (
    <>
      <ConfirmModalStyles />

      <StyledModal open={isShareLinkBoardModalOpen}
        onCancel={handleClose}
        footer={(
          <FooterContainer>

            <StyledButton
              key="delete"
              onClick={handleDeleteLink}
              icon={<DisconnectOutlined />}
            >
              Delete link
            </StyledButton>

            <StyledButton
              key="copy"
              type="primary"
              onClick={handleCopyLink}
              disabled={!publicLink}
              icon={<CopyOutlined />}
            >
              Copy link
            </StyledButton>

          </FooterContainer>
        )}
        title="Share link"
        width={480}
        closable={true}
        destroyOnClose={true}
      >
        <SettingsTab
          itemType={sharedItemData?.type}
          id={sharedItemData?.id}
          onPendingCollaboratorChange={setPendingCollaborator}
        />
      </StyledModal>
    </>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-header {
    margin-bottom: 0;
  }

  .ant-modal-body {
    gap: 20px;
    display: flex;
    flex-direction: column;
  }
`

const FooterContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
  justify-content: space-between;
`

const StyledButton = styled(Button)`
  font-size: 16px;
  height: 40px;
`